import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  destroyPdDevice,
  ensurePdDevice,
  getPdDevice,
  setPdTokenRefreshCallback,
} from '@/lib/twilioPdDeviceSingleton'
import { usePowerDialerVoiceToken } from '@/api/voice'

/**
 * Hook lifecycle React pour le 2e Device Twilio enregistré sur le subaccount
 * Power Dialer. Symétrique à useTwilioDevice mais conditionnel (`enabled`).
 *
 * À utiliser dans les vues Power Dialer qui ont besoin de :
 *  - Passer des calls outbound depuis un numéro PD (caller_id subaccount)
 *  - Recevoir les rappels de prospects (incoming routés vers le subaccount)
 *
 * Le Device est créé uniquement quand `enabled=true` (i.e. l'user est dans une
 * session PD). Au unmount ou quand enabled passe à false, le Device est
 * détruit pour libérer les ressources et la binding Twilio.
 *
 * Si la company n'a pas de subaccount configuré, le hook ne fait rien
 * (l'API retourne 400, l'erreur est silencieuse — pas tous les comptes ont PD).
 */
export function usePdTwilioDevice(options: { enabled: boolean }): {
  ready: boolean
} {
  const { enabled } = options
  const queryClient = useQueryClient()
  const tokenQuery = usePowerDialerVoiceToken(enabled)
  const [ready, setReady] = useState(false)
  const isInitializedRef = useRef(false)

  // Init device dès qu'on a un token et que enabled=true
  useEffect(() => {
    if (!enabled || !tokenQuery.data?.token || isInitializedRef.current) return
    isInitializedRef.current = true

    let cancelled = false

    void (async () => {
      try {
        const device = await ensurePdDevice(tokenQuery.data.token)
        if (cancelled) return

        device.removeAllListeners('registered')
        device.removeAllListeners('unregistered')

        device.on('registered', () => setReady(true))
        device.on('unregistered', () => setReady(false))

        setPdTokenRefreshCallback(async () => {
          await queryClient.invalidateQueries({ queryKey: ['pd-voice-token'] })
        })

        if (device.state === 'unregistered') {
          await device.register()
        } else if (device.state === 'registered') {
          setReady(true)
        }
      } catch (err) {
        console.error('[usePdTwilioDevice] init failed:', err)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [enabled, tokenQuery.data?.token, queryClient])

  // Refresh token sur device existant
  useEffect(() => {
    const device = getPdDevice()
    if (!device || !tokenQuery.data?.token || !isInitializedRef.current) return
    if (device.state === 'registered') {
      try {
        device.updateToken(tokenQuery.data.token)
      } catch (err) {
        console.error('[usePdTwilioDevice] updateToken failed:', err)
      }
    }
  }, [tokenQuery.data?.token])

  // Cleanup : détruire le Device dès que enabled passe à false ou au unmount
  useEffect(() => {
    if (enabled) return
    return () => {
      if (getPdDevice()) {
        destroyPdDevice()
        setPdTokenRefreshCallback(null)
        isInitializedRef.current = false
        setReady(false)
      }
    }
  }, [enabled])

  return { ready }
}
